import { useRouter } from 'next/router'
import { Box, Container, Skeleton, Typography } from '@mui/material'
import ContentfulSearchCarousel from '../../components/ContentfulComponents/ContentfulSearchCarousel'
import FeaturedContentGrid from '../../components/ContentfulComponents/FeaturedContentGrid'
import Layout from '../../components/layout'
import ProfileHeader from '../../components/ProfileHeader'
import ReadMore from '../../components/ReadMore'
import SEOHead from '../../components/SEOHead'
import Topics from '../../components/Topics'
import TwoColumnCta from '../../components/TwoColumnCta'
import VideoPlaylist from '../../components/VideoComponents/VideoPlaylist'
import CustomBlock from '../../const/CustomBlocks'
import { getContentfulDirectClient } from '../../lib/apollo-client'
import { contentfulAPIToSEOHead } from '../../lib/data-transformations'
import GET_PROFILE from '../../lib/schema/pages/getProfile.graphql'
import GET_PROFILE_CONTENT_FIVE from '../../lib/schema/pages/getProfileContentFive.graphql'
import GET_PROFILE_CONTENT_FOUR from '../../lib/schema/pages/getProfileContentFour.graphql'
import GET_PROFILE_CONTENT_ONE from '../../lib/schema/pages/getProfileContentOne.graphql'
import GET_PROFILE_CONTENT_THREE from '../../lib/schema/pages/getProfileContentThree.graphql'
import GET_PROFILE_CONTENT_TWO from '../../lib/schema/pages/getProfileContentTwo.graphql'
import GET_PROFILE_LIST_QUERY from '../../lib/schema/pages/getProfileList.graphql'
import GET_PROFILE_SUMMARY from '../../lib/schema/pages/getProfileSummary.graphql'
import { reconcilePageData } from '../../lib/utils'
import paths from '../../paths/path'

export default function AuthorDetails({
  algoliaIndexId,
  expertise,
  areasOfExpertise,
  seo,
  description,
  firstName,
  lastName,
  thumbnail,
  role,
  experience,
  position,
  contents,
  videos,
  bookSize,
  webinarSize,
  articleSize,
  podcastSize,
  blogSize,
  title,
}) {
  const router = useRouter()
  if (router.isFallback) {
    return (
      <Skeleton
        animation='wave'
        variant='rectangular'
        width='100%'
        height='100px'
      />
    )
  }

  const primaryTopics = expertise
    ? expertise?.items
        ?.filter((item) => !!item)
        ?.reduce((unique, o) => {
          if (!unique.includes(o)) {
            unique.push(o)
          }
          return unique
        }, [])
        .sort()
    : null

  const expertises = areasOfExpertise
    ? areasOfExpertise?.items
        ?.filter((item) => !!item)
        .reduce((unique, o) => {
          if (!unique.includes(o)) {
            unique.push(o)
          }
          return unique
        }, [])
        .sort()
    : null

  return (
    <Layout>
      <SEOHead seo={seo} />
      <ProfileHeader
        thumbnail={thumbnail}
        firstName={firstName}
        lastName={lastName}
        role={role}
        experience={experience}
        position={position}
      />

      <Container maxWidth='lg'>
        <Box
          sx={{
            width: '100%',
            maxWidth: '850px',
            margin: 'auto',
          }}
        >
          <Box mt={10} id='about'>
            <ReadMore
              title='About'
              short={description?.json}
              links={description?.links}
              textAlign='left'
              titleVariant='h4'
              hideSummaryWhenExpanded
            />
          </Box>
          {primaryTopics?.length > 0 && (
            <Box mt={10} width='100%'>
              <Topics
                testId='primary-topics'
                title='Primary Topics'
                topics={primaryTopics}
                titleVariant='h4'
              />
            </Box>
          )}
          <Container maxWidth='lg'>
            {contents?.items?.map((component, index) => {
              return (
                <Box key={index} mt={[5, 10, 15]} mb={[5, 10, 15]}>
                  <CustomBlock item={component} />
                </Box>
              )
            })}
          </Container>
          {expertises?.length > 0 && (
            <Box mt={10} width='100%'>
              <Topics
                testId='expertise-topics'
                title='Areas Of Expertise'
                topics={expertises}
                filter='keywords'
                titleVariant='h4'
              />
            </Box>
          )}
        </Box>
      </Container>
      {bookSize > 0 && (
        <Box
          mt={10}
          sx={{
            width: '100%',
            bgcolor: 'background.lightGrey',
            borderBottom: '2px solid white',
          }}
        >
          <Container maxWidth='lg' sx={{ paddingTop: [3, 5] }}>
            <ContentfulSearchCarousel
              testId={'books-contentful-search-carousel'}
              indexName={algoliaIndexId}
              title={`Books by ${firstName} ${lastName}`}
              scrollByAmount={4}
              type={'book'}
              authors={[`${title}`]}
              ctaUrlLink={paths.search({
                types: ['book'],
                authors: [`${firstName} ${lastName}`],
              })}
              isAutoShowingCta={true}
              totalLength={bookSize}
            />
          </Container>
        </Box>
      )}
      {webinarSize > 0 && (
        <Box
          sx={(theme) => ({
            width: '100%',
            borderBottom: `2px solid ${theme.palette.background.lightGrey}`,
          })}
        >
          <Container maxWidth='lg' sx={{ paddingTop: [3, 5] }}>
            <ContentfulSearchCarousel
              testId={'webinars-contentful-search-carousel'}
              indexName={algoliaIndexId}
              title={`Webinars by ${firstName} ${lastName}`}
              scrollByAmount={4}
              type={'webinar'}
              authors={[`${title}`]}
              ctaUrlLink={paths.search({
                types: ['webinar'],
                authors: [`${firstName} ${lastName}`],
              })}
              isAutoShowingCta={true}
              totalLength={webinarSize}
            />
          </Container>
        </Box>
      )}
      {blogSize > 0 && (
        <Box
          sx={{
            width: '100%',
            bgcolor: 'background.lightGrey',
            borderBottom: '2px solid white',
          }}
        >
          <Container
            maxWidth='lg'
            sx={{ paddingTop: '1px', paddingBottom: ['24px', '40px', '90px'] }}
          >
            <FeaturedContentGrid
              testId={'blogs-featured-content-grid'}
              indexName={algoliaIndexId}
              displayTitle={`Blogs by ${firstName} ${lastName}`}
              contentTypes={['blog']}
              authors={[`${title}`]}
              isAutoShowingCta={true}
              totalLength={blogSize}
            />
          </Container>
        </Box>
      )}
      {articleSize > 0 && (
        <Box
          sx={(theme) => ({
            width: '100%',
            borderBottom: `2px solid ${theme.palette.background.lightGrey}`,
          })}
        >
          <Container maxWidth='lg' sx={{ paddingTop: [3, 5] }}>
            <ContentfulSearchCarousel
              testId={'articles-contentful-search-carousel'}
              indexName={algoliaIndexId}
              title={`Articles by ${firstName} ${lastName}`}
              scrollByAmount={4}
              type={'article'}
              authors={[`${title}`]}
              ctaUrlLink={paths.search({
                types: ['article'],
                authors: [`${firstName} ${lastName}`],
              })}
              isAutoShowingCta={true}
              totalLength={articleSize}
            />
          </Container>
        </Box>
      )}
      {podcastSize > 0 && (
        <Box
          sx={{
            width: '100%',
            bgcolor: 'background.lightGrey',
            borderBottom: '2px solid white',
          }}
        >
          <Container maxWidth='lg' sx={{ paddingTop: '1px' }}>
            <FeaturedContentGrid
              testId={'podcasts-featured-content-grid'}
              indexName={algoliaIndexId}
              displayTitle={`Podcasts by ${firstName} ${lastName}`}
              contentTypes={['podcast']}
              authors={[`${title}`]}
              isAutoShowingCta={true}
              totalLength={podcastSize}
            />
          </Container>
        </Box>
      )}

      <Container maxWidth='lg'>
        {videos.length > 0 && (
          <Box mt={10} width='100%'>
            <Typography variant='h4'>{`Videos by ${firstName} ${lastName}`}</Typography>
            {!!videos[0] && (
              <VideoPlaylist
                topic={videos[0]?.topic}
                videoId={videos[0]?.videoId}
                premium={videos[0]?.premium}
                title={videos[0]?.title}
                date={videos[0]?.date}
                videos={videos}
                type='video'
              />
            )}
          </Box>
        )}

        <Box my={10}>
          {
            <TwoColumnCta
              testId='people-write-for-2-col-cta'
              title='Write for ASCD'
              description='We publish insightful, actionable, relevant work from educators across all levels of education that help educators learn, teach and lead.'
              ctaItems={[
                {
                  __typename: 'ButtonLinkComponent',
                  linkUrl: '/write-for-ascd',
                  linkLabel: 'Learn more',
                },
              ]}
              image='/images/write_for_ascd.svg'
              imagePos='right'
              variant='grey'
            />
          }
        </Box>
      </Container>
    </Layout>
  )
}

export async function getStaticPaths() {
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: { profiles: profileData },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_LIST_QUERY,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: Number(process.env.NEXT_STATIC_BUILD_LIMIT || 200),
    },
  })

  return {
    paths: profileData?.items
      ?.filter((item) => !!item.slug)
      ?.map((item) => ({
        params: { profile: item.slug },
      })),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: { profiles },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      videoLimit: 10,
      where: { slug: params.profile },
    },
  })

  const {
    data: { profiles: profileSummary },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_SUMMARY,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.profile },
    },
  })

  if (!profiles || !profiles?.items?.length) {
    return {
      notFound: true,
    }
  }

  const {
    profileType,
    expertise,
    areasOfExpertise,
    seo,
    instagram,
    twitter,
    facebook,
    youTube,
    linkedIn,
    role,
    experience,
    position,
    __typename,
    linkedContent: { blogs, articles, videos, books, podcasts, webinars },
  } = profiles?.items[0] || {}

  const { description, title, sys, firstName, lastName, email, thumbnail } =
    profileSummary?.items[0] || {}

  const algoliaIndexId = process.env.NEXT_PUBLIC_ALGOLIA_INDEX_ID

  //getting additional content blocks
  const {
    data: {
      profiles: { items: contentsOne },
    },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_CONTENT_ONE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.profile },
    },
  })

  const {
    data: {
      profiles: { items: contentsTwo },
    },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_CONTENT_TWO,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.profile },
    },
  })

  const {
    data: {
      profiles: { items: contentsThree },
    },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_CONTENT_THREE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.profile },
    },
  })

  const {
    data: {
      profiles: { items: contentsFive },
    },
  } = await contentfulDirectClient.query({
    query: GET_PROFILE_CONTENT_FIVE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      memberPriceItemCollectionLimit: 10,
      memberPriceCollectionLimit: 6,
      where: { slug: params.profile },
    },
  })

  /** rich text component is 'too complex' for single query here */
  let promiseChain = []

  for (let i = 0; i < 5; i++) {
    promiseChain.push([
      await contentfulDirectClient.query({
        query: GET_PROFILE_CONTENT_FOUR,
        variables: {
          preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
          skip: 10 * i,
          limit: 1,
          contentCollectionLimit: 10,
          where: { slug: params.profile },
        },
      }),
    ])
  }

  const itemsData = await Promise.all(promiseChain)

  const restItems = [...itemsData.flat()]
    .map((items) => items?.data?.profiles.items?.[0]?.content?.items)
    .flat()

  const contentsFour = [{ content: { items: [...restItems] } }]

  const contents = reconcilePageData(
    contentsOne,
    contentsTwo,
    contentsThree,
    contentsFour,
    contentsFive
  )
  return {
    props: {
      title,
      algoliaIndexId,
      profileType,
      expertise,
      areasOfExpertise,
      seo:
        seo ||
        contentfulAPIToSEOHead({
          title,
          description: description?.json,
          thumbnail,
          sys,
          __typename,
        }),
      description,
      firstName,
      lastName,
      thumbnail,
      email,
      instagram,
      twitter,
      facebook,
      youTube,
      linkedIn,
      role,
      experience,
      position,
      contents: contents?.content || [],
      /**
       * These contents cannot be ordered from linkedFrom in profiles,
       * and there is no other query possible to grab content via the author sys id.
       * Thus, we need to grab more than possible of the content and order and slice
       * it ourselves.
       */
      videos: videos?.items.filter((item) => !!item) || [],
      articleSize: articles?.total,
      blogSize: blogs?.total,
      bookSize: books?.total,
      podcastSize: podcasts?.total,
      webinarSize: webinars?.total,
    },
    revalidate: 20,
  }
}
